import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTheme } from '@course-authoring/shared-types';

export interface DropdownOption<T extends string = string> {
  value: T;
  label: string;
}

export interface DropdownProps<T extends string = string> {
  /** Currently selected value. */
  value: T;
  /** Selectable options, in display order. */
  options: readonly DropdownOption<T>[];
  onChange: (value: T) => void;
  /** Accessible label for the trigger (and the listbox). */
  ariaLabel?: string;
  /** Muted trigger text shown while `value` matches no option. */
  placeholder?: string;
  /** Disables the trigger — no open, default cursor. */
  disabled?: boolean;
  /**
   * Style overrides merged over the default trigger styling (sizing, border
   * tints, corner shaping). The base uses longhand border properties so
   * per-state `borderColor` overrides compose without shorthand conflicts.
   */
  triggerStyle?: React.CSSProperties;
  'data-testid'?: string;
}

/**
 * Shared single-select dropdown primitive for learner-facing renderers.
 *
 * A styled trigger button that toggles an absolutely-positioned `listbox`
 * menu — the custom (non-native) control the design system's "Dropdown" symbol
 * calls for. Closes on outside pointer-down and Escape. Modeled on the Map
 * block's dropdown, minus the typeahead filtering.
 */
export function Dropdown<T extends string = string>({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  disabled = false,
  triggerStyle,
  'data-testid': dataTestId,
}: DropdownProps<T>): React.ReactElement {
  const { tokens } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredValue, setHoveredValue] = useState<T | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();

  const selected = options.find((option) => option.value === value) ?? null;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (next: T) => {
      onChange(next);
      setIsOpen(false);
    },
    [onChange],
  );

  const resolvedTriggerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.sm,
    width: '100%',
    minHeight: '54px',
    padding: `0 ${tokens.spacing.lg}`,
    backgroundColor: tokens.backgrounds.app,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: tokens.borders.default,
    borderRadius: tokens.radius.sm,
    fontSize: '20px',
    fontWeight: tokens.typography.weights.semibold,
    lineHeight: 1.2,
    fontFamily: tokens.typography.fontFamily,
    color: tokens.text.primary,
    cursor: disabled ? 'default' : 'pointer',
    textAlign: 'left',
    appearance: 'none',
    boxSizing: 'border-box',
    ...triggerStyle,
  };

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    margin: 0,
    padding: `${tokens.spacing.xs} 0`,
    listStyle: 'none',
    backgroundColor: tokens.backgrounds.app,
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: tokens.radius.sm,
    boxShadow: tokens.shadow.lg,
    zIndex: 20,
    maxHeight: '260px',
    overflowY: 'auto',
    boxSizing: 'border-box',
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }} data-testid={dataTestId}>
      <button
        type="button"
        style={resolvedTriggerStyle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(selected ? {} : { color: tokens.text.muted }),
          }}
        >
          {selected?.label ?? placeholder ?? ''}
        </span>
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            flexShrink: 0,
            color: tokens.text.muted,
            transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: `transform ${tokens.ui.transitionDefault}`,
          }}
        >
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path
              d="M1 1.5L6 6.5L11 1.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen ? (
        <ul id={listboxId} style={menuStyle} role="listbox" aria-label={ariaLabel}>
          {options.map((option) => {
            const isSelected = option.value === value;
            const isHovered = hoveredValue === option.value;

            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHoveredValue(option.value)}
                  onMouseLeave={() => setHoveredValue(null)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
                    border: 'none',
                    backgroundColor:
                      isSelected || isHovered ? tokens.choice.hover.bg : 'transparent',
                    color: tokens.text.primary,
                    fontSize: '18px',
                    fontWeight: isSelected
                      ? tokens.typography.weights.semibold
                      : tokens.typography.weights.regular,
                    lineHeight: 1.2,
                    fontFamily: tokens.typography.fontFamily,
                    textAlign: 'left',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

Dropdown.displayName = 'Dropdown';
